import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  ContactSupport as ContactSupportIcon,
  Description as DescriptionIcon,
  HelpOutline as HelpOutlineIcon,
  ListAlt as ListAltIcon,
  SupportAgent as SupportAgentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { helpApi } from '../../api/helpApi';

const SUPPORT_CONTACT = {
  email: 'support@evitasoftech.com',
  phone: '+91 77740 00228',
  address: 'Mumbai, India',
};

const MODULE_OPTIONS = [
  'Attendance',
  'Payroll',
  'Login',
  'Employee Profile',
  'Leave',
  'Location',
  'HRMS Functionality',
  'Other',
];

const PRIORITY_OPTIONS = [
  { value: 0, label: 'Low' },
  { value: 1, label: 'Medium' },
  { value: 2, label: 'High' },
];

const HelpMenu: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: user?.fullName || '',
    email: user?.email || '',
    module: '',
    subject: '',
    description: '',
    priority: 1,
  });

  const canSubmitTicket = useMemo(
    () => Boolean(user?.tenantId) && !(user?.roles ?? []).some((role) => role.toLowerCase() === 'superadmin'),
    [user?.roles, user?.tenantId]
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const resetForm = () => {
    setForm({
      name: user?.fullName || '',
      email: user?.email || '',
      module: '',
      subject: '',
      description: '',
      priority: 1,
    });
  };

  const handleSubmitTicket = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.description.trim()) {
      toast.error('Please complete all required fields.');
      return;
    }

    try {
      setSubmitting(true);
      await helpApi.createTicket({
        name: form.name.trim(),
        email: form.email.trim(),
        module: form.module || 'Other',
        subject: form.subject.trim(),
        description: form.description.trim(),
        priority: form.priority,
      });
      toast.success('Support ticket submitted successfully.');
      setTicketOpen(false);
      resetForm();
      navigate('/help/my-tickets');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to submit support ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleMenuOpen} aria-label="Help menu">
        <HelpOutlineIcon />
      </IconButton>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => { handleMenuClose(); setContactOpen(true); }}>
          <ListItemIcon>
            <ContactSupportIcon fontSize="small" />
          </ListItemIcon>
          Contact
        </MenuItem>
        <MenuItem onClick={() => { handleMenuClose(); window.open('/admin-manual.html', '_blank', 'noopener,noreferrer'); }}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          Admin Manual
        </MenuItem>
        {canSubmitTicket && (
          <MenuItem onClick={() => { handleMenuClose(); setTicketOpen(true); }}>
            <ListItemIcon>
              <SupportAgentIcon fontSize="small" />
            </ListItemIcon>
            Submit a Ticket
          </MenuItem>
        )}
        {canSubmitTicket && (
          <MenuItem onClick={() => { handleMenuClose(); navigate('/help/my-tickets'); }}>
            <ListItemIcon>
              <ListAltIcon fontSize="small" />
            </ListItemIcon>
            My Tickets
          </MenuItem>
        )}
      </Menu>

      <Dialog open={contactOpen} onClose={() => setContactOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Support Contact</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Reach out to the HRMS support team using the details below.
          </Typography>
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Support Email</Typography>
              <Typography variant="body1">
                <Link href={`mailto:${SUPPORT_CONTACT.email}`} underline="hover" color="inherit">
                  {SUPPORT_CONTACT.email}
                </Link>
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Support Phone</Typography>
              <Typography variant="body1">
                <Link href={`tel:${SUPPORT_CONTACT.phone}`} underline="hover" color="inherit">
                  {SUPPORT_CONTACT.phone}
                </Link>
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Office Address</Typography>
              <Typography variant="body1">{SUPPORT_CONTACT.address}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={ticketOpen}
        onClose={() => {
          setTicketOpen(false);
          resetForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Submit a Ticket</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
            <Alert severity="info">
              This ticket will be created for your current tenant and will appear in your My Tickets list.
            </Alert>
            <TextField
              fullWidth
              label="Name"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={form.email}
              onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Issue Type</InputLabel>
              <Select
                value={form.module}
                label="Issue Type"
                onChange={(event) => setForm((prev) => ({ ...prev, module: event.target.value }))}
              >
                {MODULE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={form.priority}
                label="Priority"
                onChange={(event) => setForm((prev) => ({ ...prev, priority: Number(event.target.value) }))}
              >
                {PRIORITY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Subject"
              value={form.subject}
              onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
            />
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Description"
              value={form.description}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTicketOpen(false);
              resetForm();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmitTicket} disabled={submitting}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HelpMenu;
